import { useAuth } from '@/lib/auth'
import { captureException } from '@/lib/errors'
import { useMutation } from '@/lib/mutations'
import { offlineExecutor } from '@/lib/offline'
import { useStore } from '@/lib/pocketbase'
import type { TimeEntries } from '@/types/pbSchema'

interface UpdateTimeEntryData {
    entryId: string
    orgId: string
    previousEntry: Partial<TimeEntries>
    job: string
    location?: string
    clock_in: string
    clock_out?: string
    duration?: number
    notes?: string
    entry_type?: 'manual' | 'break'
}

export function useUpdateTimeEntry() {
    const { user } = useAuth()
    const [timeEntriesCollection] = useStore('time_entries')

    const mutation = useMutation({
        mutationFn: async (data: UpdateTimeEntryData) => {
            if (!user?.id) {
                throw new Error('User not found')
            }

            const { entryId, orgId: _orgId, previousEntry: _prev, ...updateData } = data

            const updatedFields = {
                ...updateData,
                location: updateData.location || '',
                status: 'pending' as const,
                modified_by: user.id,
            }

            const tx = offlineExecutor.createOfflineTransaction({
                mutationFnName: 'syncTimeEntries',
            })
            tx.mutate(() => {
                timeEntriesCollection.update(entryId, draft => {
                    Object.assign(draft, updatedFields)
                })
            })
        },
        onError: error => {
            captureException('Update time entry failed', error)
        },
    })

    return {
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
        isPending: mutation.isPending,
    }
}
